import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../components/mui/Buttons/Buttons';
import { getAchievementBadges } from '../../redux/features/achievementBadgeSlice';
import { AchievementBadgeTableHeader } from './components/AchievementBadgeTableHeader';
import AchievementBadgeAddEdit from './components/AchievementBadgeAddEdit';
import DeleteAchievementBadge from './components/DeleteAchievementBadge';

const AchievementBadges = () => {
  const dispatch = useDispatch();
  const { data, meta, loading } = useSelector((state) => state.achievementBadge.table);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    sort: { field: '', direction: 'asc' },
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const [modalState, setModalState] = useState({ actionKey: '', row: null });

  // Params the LIST endpoint understands (see the API doc).
  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
      sortBy: tableState.sort?.field || undefined,
      sortDirection: tableState.sort?.direction || 'asc',
    }),
    [tableState.page, tableState.pageSize, tableState.search, tableState.sort],
  );

  useEffect(() => {
    dispatch(getAchievementBadges(dispatchParams));
  }, [dispatch, dispatchParams]);

  const handleRefresh = useCallback(() => {
    dispatch(getAchievementBadges({ ...dispatchParams, hardRefresh: true }));
  }, [dispatch, dispatchParams]);

  // Close a modal; refetch when a mutation succeeded.
  const closeModal = useCallback(
    (shouldRefresh = false) => {
      setModalState({ actionKey: '', row: null });
      if (shouldRefresh) handleRefresh();
    },
    [handleRefresh],
  );

  const headers = useMemo(() => AchievementBadgeTableHeader(), []);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;
    setModalState({ actionKey, row });
  }, []);

  const modalConfigs = useMemo(
    () => [
      { actionKey: 'addBadge', title: 'Add Badge', render: () => <AchievementBadgeAddEdit onClose={closeModal} /> },
      {
        actionKey: 'editBadge',
        title: 'Edit Badge',
        render: ({ row }) => <AchievementBadgeAddEdit row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'deleteBadge',
        title: 'Delete Badge',
        render: ({ row }) => <DeleteAchievementBadge row={row} onClose={closeModal} />,
      },
    ],
    [closeModal],
  );

  const activeConfig = useMemo(
    () => modalConfigs.find((config) => config.actionKey === modalState.actionKey),
    [modalConfigs, modalState.actionKey],
  );

  return (
    <GlobalWrapper title={'Achievement Badges'}>
      <Box mt={1}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <AddButton action={() => setModalState({ actionKey: 'addBadge', row: null })}>Add Badge</AddButton>
        </Stack>

        <Paper elevation={3}>
          <Box mt={1}>
            <GlobalTable
              rowUniqueId="badge_id"
              headers={headers}
              tableState={tableState}
              setTableState={setTableState}
              loading={loading}
              rows={data}
              meta={meta}
              onButtonActionClick={handleButtonActionClick}
              exportUrl="achievement-badges"
              exportParams={{ ...dispatchParams, page: 1, limit: 'no' }}
            />
          </Box>

          <GlobalDialog
            title={activeConfig?.title}
            open={Boolean(modalState?.actionKey)}
            setOpen={(val) => {
              if (!val) setModalState({ actionKey: '', row: null });
            }}
            closeButton={true}
            content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
            maxWidth={'sm'}
            fullWidth={true}
          />
        </Paper>
      </Box>
    </GlobalWrapper>
  );
};

export default AchievementBadges;
